import AddItemBtn from '@/components/atoms/AddItemBtn';
import Button from '@/components/atoms/Button';
import InfoText from '@/components/atoms/InfoText';
import ModalWithHeader from '@/components/atoms/CustomModal';
import FormField from '@/components/FormField/FormField';
import EmptyState from '@/components/SellerProfileCreation/EmptyState';
import { closeModal, openModal, useModal } from '@/store/slices/Modals';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { z } from 'zod';
import './styles.css';
import SpecsTable from '@/components/Sellers/Profile/Products/AddProduct/components/SpecsTable';

const modalId = 'SPECS_MODAL';

export default function ProductSpecs({
  preview = false,
}: {
  preview?: boolean;
}) {
  const { t } = useTranslation();
  const required_error = t('validations.requiredField');
  const dispatch = useDispatch();
  const formContext = useFormContext();
  const { modals } = useModal();

  const modalData = modals[modalId]?.data;
  const specsValue = formContext.watch('specs');

  const schema = z.object({
    type: z
      .string({ required_error })
      .trim()
      .min(2, t('validations.minLength', { value: 2 }))
      .max(50, t('validations.maxLength', { value: 50 })),
    value: z
      .string({ required_error })
      .trim()
      .min(2, t('validations.minLength', { value: 2 }))
      .max(50, t('validations.maxLength', { value: 50 })),
  });

  type schemaType = z.infer<typeof schema>;

  const methods = useForm<schemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    values: {
      type: modalData?.type?.toString() as string,
      value: modalData?.value?.toString() as string,
    },
  });

  const onSubmit = (data: schemaType) => {
    // Incase of Edit
    const specs = formContext.getValues()?.specs;
    if (!!modalData) {
      specs[modalData?.index as number] = data;
    } else {
      specs.push(data);
    }
    formContext.setValue('specs', specs);
    dispatch(closeModal({ id: modalId }));
    methods.reset();
    formContext.trigger('specs');
  };

  return (
    <div
      id="specs"
      data-testid="bidding-post-product-specs"
      className="product-specs"
    >
      <div className="flex flex-col gap-2 mb-4">
        <div
          className="flex items-center justify-between"
          data-testid="bidding-post-product-specs-header"
        >
          <InfoText
            text={t('biddingPost.productSpecs')}
            className="!font-bold !text-neutral-900 dark:!text-dark-text-primary"
            size="base"
          />

          {!preview && specsValue?.length < 10 && (
            <AddItemBtn
              onClick={() => {
                dispatch(openModal({ id: modalId }));
              }}
              text={t('biddingPost.addSpecs')}
              id="specs_modal"
            />
          )}
        </div>

        {/* Error if exist */}
        {formContext?.formState.errors.specs && (
          <InfoText
            size="xs"
            className="!text-red-500 self-end text-right"
            text={
              formContext?.formState.errors.specs.message?.toString() as string
            }
          />
        )}
      </div>

      {/* Specs table */}
      {!!specsValue?.length ? <SpecsTable preview={preview} /> : <EmptyState />}

      {/* Specs form */}
      {modals?.[modalId]?.isOpen && (
        <ModalWithHeader
          centered
          isOpen={true}
          title={
            modalData
              ? t('biddingPost.editProductSpecs')
              : t('biddingPost.addProductSpecs')
          }
          description={t('biddingPost.addProductSpecsDesc')}
          onCancel={() => {
            methods.setValue('type', '');
            methods.setValue('value', '');
            dispatch(closeModal({ id: modalId }));
          }}
        >
          <FormProvider {...methods}>
            <form
              className="flex flex-col gap-6 sm:grid sm:grid-cols-2"
              data-testid="bidding-post-product-specs-modal-form"
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                methods.handleSubmit(onSubmit)(e);
              }}
            >
              <FormField
                type="text"
                name="type"
                id="type"
                label={t('biddingPost.specType')}
                placeholder={t('biddingPost.typeProductSpec')}
              />

              <FormField
                type="text"
                name="value"
                id="value"
                label={t('biddingPost.specValue')}
                placeholder={t('biddingPost.typeSpecValue')}
              />

              <div
                className="col-span-2 flex justify-end"
                data-testid="bidding-post-product-specs-modal-actions"
              >
                <Button
                  type="submit"
                  data-testid="bidding-post-product-specs-modal-submit"
                  className="!px-4 sm:!px-6 !py-2.5 sm:!py-3"
                >
                  {t('biddingPost.done')}
                </Button>
              </div>
            </form>
          </FormProvider>
        </ModalWithHeader>
      )}
    </div>
  );
}
